/**
 * Offline cache — Phase 1: read-only mirror of the most-fetched API
 * payloads (products, locations, categories, suppliers).
 *
 * Backed by IndexedDB via `idb-keyval` so the cache survives page
 * reloads, browser restarts, and Capacitor app restarts on iOS/Android.
 *
 * Strategy: write-through on every successful GET, read-fallback when
 * the network is offline OR the fetch itself fails. NO writes are
 * mirrored in Phase 1 — mutations are blocked at the UI level via
 * `useOfflineGate`.
 *
 * Cache keys are namespaced (`tsp:products`, `tsp:locations`, etc.) so
 * the user's localStorage and other libraries' IDB stores stay clean.
 */
import { createStore, get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';

const STORE = createStore('tsp-offline-cache', 'kv');

const KEYS = {
  products: 'tsp:products',
  locations: 'tsp:locations',
  categories: 'tsp:categories',
  suppliers: 'tsp:suppliers',
  dashboardStats: 'tsp:dashboard:stats',
  // List endpoints — cached by signature so different filter combos
  // get their own slot (otherwise the cache would constantly thrash
  // when a user switches filters online).
  transactions: 'tsp:transactions:default',  // last 50 default
  reportInventory: 'tsp:report:inventory',
  reportLowStock: 'tsp:report:low-stock',
  reportMovements: 'tsp:report:movements',
  reportUserActivity: 'tsp:report:user-activity',
  reportMoveSummary: 'tsp:report:move-summary',
};

/**
 * Write a successful response into the cache. Wrapped in try/catch
 * so a quota error or private-browsing IDB block never breaks the
 * online path — failing to cache is acceptable, failing to render
 * the data the user just fetched is not.
 */
export async function writeCache(key, data) {
  if (!data) return;
  try {
    await idbSet(key, {
      data,
      cached_at: new Date().toISOString(),
    }, STORE);
  } catch (e) {
    // IDB blocked / quota full — degrade silently.
    console.warn('[offlineCache] write failed', key, e?.message);
  }
}

export async function readCache(key) {
  try {
    const entry = await idbGet(key, STORE);
    return entry || null;
  } catch (e) {
    console.warn('[offlineCache] read failed', key, e?.message);
    return null;
  }
}

export async function clearCache(key) {
  try {
    if (key) await idbDel(key, STORE);
  } catch (_) { /* ignore */ }
}

export const CACHE_KEYS = KEYS;

/**
 * Sniff whether the device is online RIGHT NOW. We don't trust
 * `navigator.onLine` alone (it lies on iOS Safari + some Android
 * WebViews) so callers should pair this with a real fetch attempt.
 */
export const isLikelyOnline = () => {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
};
